import axios from 'axios';
import { debugLog, debugWarn } from './debug';

// Backend URL configuration
let BACKEND_URL;

// Determine API URL based on environment
if (process.env.REACT_APP_API_URL) {
  // Use environment variable if provided (production or staging)
  BACKEND_URL = process.env.REACT_APP_API_URL;
} else if (process.env.NEXT_PUBLIC_API_URL) {
  // For Vercel deployment
  BACKEND_URL = process.env.NEXT_PUBLIC_API_URL;
} else {
  // Default to localhost for development
  BACKEND_URL = 'http://localhost:5001';
}
const API_BASE_URL = `${BACKEND_URL}/api`;
const AUTH_EXPIRED_EVENT = 'fashionmate:auth-expired';
let authExpiryDispatched = false;

// Create an Axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  // Longer timeout for potentially slow LLM responses
  timeout: 60000,
});

// Keep network failures separate from expired authentication.
api.interceptors.response.use(
  response => {
    if (response.config?.url?.includes('/auth/google-login')) authExpiryDispatched = false;
    return response;
  },
  error => {
    debugWarn('API request failed', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      code: error.code,
    });

    const isLoginAttempt = error.config?.url?.includes('/auth/google-login');
    if (error.response?.status === 401 && !isLoginAttempt) {
      localStorage.removeItem('auth_token');
      delete api.defaults.headers.common.Authorization;
      error.isAuthError = true;
      error.userMessage = 'Your session expired. Sign in again.';
      if (!authExpiryDispatched) {
        authExpiryDispatched = true;
        window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, {
          detail: { returnTo: `${window.location.pathname}${window.location.search}` },
        }));
      }
      return Promise.reject(error);
    }

    if (error.config?.url?.includes('/chat') && (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK')) {
      error.userMessage = 'The AI service is taking longer than expected. Your session is still active, so please try again in a moment.';
    }
    if (error.code === 'ECONNABORTED') {
      error.message = 'Server taking too long to respond. It may be restarting. Please try again in a moment.';
    }
    if (error.code === 'ERR_NETWORK') {
      error.message = 'Cannot connect to server. It may be restarting. Please try again in a moment.';
    }
    return Promise.reject(error);
  }
);

// Add request interceptor to include JWT token if available
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('auth_token');
    
    debugLog(`API Request: ${config.method?.toUpperCase()} ${config.url}`, { hasToken: !!token });
    
    if (token) {
      // Make sure the token is properly set
      config.headers.Authorization = `Bearer ${token}`;
      
      // Add custom header to indicate this is an authenticated AJAX request
      // This helps the backend distinguish between browser navigations and API requests
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      
    } else {
      debugLog('No auth token found for request:', config.url);
      
      // Still mark as AJAX request for consistent error handling
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
    }
    
    return config;
  },
  error => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Helper for image URLs to ensure the correct server address
export const getFullImageUrl = (relativeUrl) => {
  // Normalize possible 'sandbox:' prefix that the backend/LLM might add to internal URLs.
  let url = relativeUrl;
  if (url && url.startsWith('sandbox:')) {
    url = url.replace(/^sandbox:/, '');
  }

  // If it's already a full URL (Firebase, external, etc.), return as-is
  if (url && url.startsWith('http')) {
    return url;
  }

  // data: and blob: URLs pass through
  if (url && (url.startsWith('data:') || url.startsWith('blob:'))) {
    return url;
  }

  // Legacy relative URLs (/api/images/... or /api/uploads/...) - prepend backend URL
  if (url && url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }

  return url;
};

// Helper for URL generation
export const getApiUrl = (path) => {
  // If it's already a full URL, return it as is
  if (path && path.startsWith('http')) {
    return path;
  }
  
  // Make sure path starts with a slash
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  // If it's an API path, construct the full URL
  return `${BACKEND_URL}/api${normalizedPath}`;
};

// Authentication API calls
export const authAPI = {
  // Initialize Google login with Google Identity Services
  loginWithGoogle: async (idToken) => {
    try {
      debugLog('Sending ID token to backend for verification...');
      const response = await api.post('/auth/google-login', { id_token: idToken });
      
      // Save token from the response
      if (response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
        api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      }
      
      return response.data;
    } catch (error) {
      console.error('Failed to authenticate with Google:', error);
      throw error;
    }
  },
  
  // Legacy login method (for backward compatibility if needed)
  login: async () => {
    try {
      // Get backend login URL (direct navigation to avoid CORS issues)
      const loginUrl = getApiUrl('/auth/login');
      
      // Use direct navigation instead of AJAX to avoid CORS issues with redirects
      debugLog('Redirecting browser to login URL:', loginUrl);
      window.location.href = loginUrl;
      
      // This is a fallback that won't execute due to page navigation
      return true;
    } catch (error) {
      console.error('Failed to initiate login:', error);
      throw error;
    }
  },
  
  // Set auth token for requests
  setAuthToken: (token) => {
    if (!token) {
      delete api.defaults.headers.common['Authorization'];
      return;
    }
    
    debugLog('Setting auth token in API defaults');
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  },
  
  // Check authentication status
  getAuthStatus: async () => {
    debugLog('Checking auth status with backend');
    return await api.get('/auth/status');
  },
  
  // Logout user
  logout: async () => {
    try {
      const response = await api.get('/auth/logout');
      // Clear token regardless of server response
      localStorage.removeItem('auth_token');
      delete api.defaults.headers.common['Authorization'];
      return response;
    } catch (error) {
      // Still clear local token on error
      localStorage.removeItem('auth_token');
      delete api.defaults.headers.common['Authorization'];
      throw error;
    }
  },
  
  // Get current user profile
  getProfile: async () => {
    return await api.get('/auth/me');
  }
};

// Closet API calls
export const closetAPI = {
  getItems: async () => {
    return await api.get('/closet/items');
  },
  addItem: async (itemData) => {
    // Make sure to convert any relative image URLs to full URLs
    const processedData = { ...itemData };
    
    // Handle both imageUrl and image_url fields
    if (processedData.imageUrl && !processedData.image_url) {
      processedData.image_url = getFullImageUrl(processedData.imageUrl);
      delete processedData.imageUrl; // Use the normalized field name
    } else if (processedData.image_url) {
      processedData.image_url = getFullImageUrl(processedData.image_url);
    }
    
    debugLog('Sending item data to server:', processedData);
    return await api.post('/closet/add', processedData);
  },
  deleteItem: async (itemId) => {
    return await api.delete(`/closet/delete/${itemId}`);
  },
  refreshCloset: async () => {
    try {
      return await api.post('/closet/refresh');
    } catch (error) {
      console.error('Error refreshing closet:', error);
      // Provide a more graceful error response
      return {
        data: { success: false, error: 'Unable to refresh closet. Try again later.' }
      };
    }
  },
  getClosetConfig: async () => {
    return await api.get('/closet/config');
  },
  updateClosetConfig: async (configData) => {
    return await api.post('/closet/config', configData);
  },
  uploadImage: async (formData) => {
    try {
      const response = await api.post('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Ensure the URL is a full URL
      if (response.data && response.data.url) {
        response.data.url = getFullImageUrl(response.data.url);
      }
      
      return response;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },
  analyzeImage: async (imageUrl) => {
    // Log what URL we're sending
    debugLog('Analyzing image with URL:', imageUrl);
    debugLog('After transformation:', getFullImageUrl(imageUrl));
    
    return await api.post('/closet/analyze-image', { 
      image_url: getFullImageUrl(imageUrl) 
    });
  },
  analyzeImageFile: async (formData) => {
    debugLog('Analyzing image file directly');

    return await api.post('/closet/analyze-image-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  bulkAdd: async (items, signal) => {
    return await api.post('/closet/bulk-add', { items }, {
      signal,
      timeout: 60000,
    });
  },
  scanCloset: async (formData) => {
    debugLog('Scanning closet photos');
    return await api.post('/closet/scan-closet', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 180000, // Allows one batch attempt plus per-photo recovery
    });
  },
  getSourcePhotos: async () => {
    return await api.get('/closet/source-photos');
  },
  deleteSourcePhoto: async (sourcePhotoId) => {
    return await api.delete(`/closet/source-photos/${sourcePhotoId}`);
  },
  getClosetLimits: async () => {
    try {
      return await api.get('/closet/limits');
    } catch (error) {
      console.error('Error fetching closet limits:', error);
      // Return a default value on error
      return {
        data: {
          success: false,
          data: {
            closet_items: { used: 0, limit: 0 },
            uploads: { used: 0, limit: 0 },
            analyses: { used: 0, limit: 0 }
          }
        }
      };
    }
  }
};

// Chat API calls
export const chatAPI = {
  sendMessage: async (message, history = [], stylePreferences = null, groundingItemIds = []) => {
    try {
      debugLog('Using authenticated chat endpoint');
      return await api.post('/chat', { 
        message, 
        history, 
        stylePreferences,
        groundingItemIds,
      }, {
        timeout: 180000, // Allow approved GPT Image generation to finish
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        }
      });
    } catch (error) {
      console.error('Error in sendMessage:', error);
      throw error;
    }
  },
  getChatLimits: async () => {
    try {
      return await api.get('/chat/limits');
    } catch (error) {
      console.error('Error fetching chat limits:', error);
      // Return a default value on error
      return {
        data: {
          success: false,
          data: {
            chat_turns: { used: 0, limit: 0 }
          }
        }
      };
    }
  }
};

// Social API calls
export const socialAPI = {
  // Feed operations
  getFeed: async () => {
    return await api.get('/social/feed');
  },
  getDiscoverFeed: async () => {
    return await api.get('/social/discover');
  },
  
  // Post operations
  createPost: async (formData) => {
    return await api.post('/social/posts', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  likePost: async (postId) => {
    return await api.post(`/social/posts/${postId}/like`);
  },
  addComment: async (postId, content) => {
    return await api.post(`/social/posts/${postId}/comments`, { content });
  },
  
  // User operations
  searchUsers: async (query) => {
    return await api.get(`/social/users/search?query=${encodeURIComponent(query)}`);
  },
  followUser: async (userId) => {
    return await api.post(`/social/users/${userId}/follow`);
  },
  
  // New follow system endpoints
  getUserProfile: async (userId) => {
    return await api.get(`/social/users/${userId}/profile`);
  },
  getFollowers: async (userId, page = 1) => {
    return await api.get(`/social/users/${userId}/followers?page=${page}`);
  },
  getFollowing: async (userId, page = 1) => {
    return await api.get(`/social/users/${userId}/following?page=${page}`);
  },
  getSuggestedUsers: async (limit = 10) => {
    return await api.get(`/social/users/suggested?limit=${limit}`);
  }
};

// Helper function for follow operations
export const toggleFollow = async (userId) => {
  try {
    const response = await api.post(`/social/users/${userId}/follow`);
    return response.data;
  } catch (error) {
    console.error('Error toggling follow:', error);
    throw error;
  }
};

// Recommendations API calls
export const recommendationsAPI = {
  getHome: () => api.get('/recommendations/home', { timeout: 1800 }),
  refresh: () => api.post('/recommendations/refresh', {}, { timeout: 8000 }),
  get: (id) => api.get(`/recommendations/${id}`),
  feedback: (id, action, reason = null) => api.post(`/recommendations/${id}/feedback`, { action, reason }),
  planToday: (context = {}) => api.post('/recommendations/plan/today', context),
  planWeek: (context = {}) => api.post('/recommendations/plan/week', context),
};

// Style API calls
export const styleAPI = {
  getPreferences: async () => {
    return await api.get('/style/preferences');
  },
  addPreference: async (preferenceData) => {
    return await api.post('/style/preferences', preferenceData);
  },
  updatePreference: async (preferenceId, preferenceData) => {
    return await api.put(`/style/preferences/${preferenceId}`, preferenceData);
  },
  deletePreference: async (preferenceId) => {
    return await api.delete(`/style/preferences/${preferenceId}`);
  },
  getRecommendations: async () => {
    try {
      const response = await api.get('/style/recommendations');
      // The backend returns data in { success: true, data: { recommendations } } format
      return {
        data: { 
          recommendations: response.data?.data?.outfits || [] 
        }
      };
    } catch (error) {
      console.error('Error getting recommendations:', error);
      throw error;
    }
  }
};

// Outfit history API calls
export const outfitAPI = {
  getHistory: async () => {
    return await api.get('/outfit/history');
  },
  addOutfit: async (outfitData) => {
    return await api.post('/outfit/history', outfitData);
  },
  updateRating: async (outfitId, ratingData) => {
    return await api.put(`/outfit/history/${outfitId}`, ratingData);
  },
  saveOutfit: async (outfitData) => {
    return await api.post('/outfits/save', outfitData);
  },
  saveOutfitWithAI: async (outfitData, aiPrompt, generatedImageUrl) => {
    return await api.post('/outfit/history', {
      ...outfitData,
      ai_prompt: aiPrompt,
      generated_image_url: generatedImageUrl
    });
  },
  updateOutfitImage: async (outfitId, aiPrompt, generatedImageUrl) => {
    return await api.put(`/outfit/history/${outfitId}`, {
      ai_prompt: aiPrompt,
      generated_image_url: generatedImageUrl
    });
  }
};

export default api; 
